import { useState } from 'react';
import type { DiscountType, PromoCode, Service } from '@salonomy/types';
import {
  Button,
  Card,
  Field,
  Modal,
  Pill,
  SegmentedControl,
  Select,
  TextInput,
  Toggle,
} from '@salonomy/ui';
import { useCreatePromoCode, usePromoCodes, useUpdatePromoCode } from '../../data/discounts.js';
import { useServices } from '../../data/services.js';
import { ApiClientError } from '../../lib/api.js';
import { money, percentFromBps } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS9 §3 — promo codes for online booking: list with the SERVER's derived usage counts (`timesUsed`),
 * create (percent/fixed value, usage limit, window, optional service scope), and edit.
 *
 * IMMUTABILITY (matches UpdatePromoCode): only the window, usage limit and active flag are editable after
 * create — the VALUE and SERVICE SCOPE are frozen (re-create a code to change them). The edit form shows
 * value + scope READ-ONLY. A 409 duplicate-code conflict surfaces inline on the create form (no toast-only
 * loss). No money is computed here — the value is config; the actual reduction is resolved server-side at
 * booking. Manager-gated: a non-manager sees an honest note.
 */

/** "20" | "17.5" → 2000 / 1750 bps; null if not a valid 0.01–100 rate. */
function percentToBps(input: string): number | null {
  const cleaned = input.replace(/[%\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0 || value > 100) return null;
  return Math.round(value * 100);
}

/** "14" | "14.50" → 1400 / 1450 pence; null if not a valid positive amount. */
function poundsToPence(input: string): number | null {
  const cleaned = input.replace(/[£,\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

function promoValueLabel(p: Pick<PromoCode, 'type' | 'valueBps' | 'valuePence'>): string {
  if (p.type === 'percent') return p.valueBps != null ? `${percentFromBps(p.valueBps)} off` : '—';
  return p.valuePence != null ? `${money(p.valuePence)} off` : '—';
}

export function PromoCodesPanel({ isFloorManager }: { isFloorManager: boolean }) {
  // Manager-gate at the boundary so the promo-code list is never fetched for a non-manager.
  if (!isFloorManager) {
    return (
      <Card>
        <div className="section-title">Promo codes</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Managing online-booking promo codes is available to salon owners and managers.
        </div>
      </Card>
    );
  }
  return <PromoCodesBody />;
}

function PromoCodesBody() {
  const toast = useToast();
  const { data: services = [] } = useServices();
  const { data, isLoading, isError } = usePromoCodes();
  const codes = data?.data ?? [];

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);

  const serviceName = (id: string | null): string =>
    id ? (services.find((s) => s.id === id)?.name ?? 'A service') : 'Any service';

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          Promo codes
        </div>
        <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
          New code
        </Button>
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Codes clients can enter when booking online. The value and service scope are fixed once a
        code is created — only the window, usage limit and active flag can be edited.
      </div>

      {isError ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Couldn’t load promo codes.
        </div>
      ) : isLoading ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Loading promo codes…
        </div>
      ) : codes.length === 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>
          No promo codes yet.
        </div>
      ) : (
        <div className="settings-list">
          {codes.map((p) => (
            <div key={p.id} className="settings-row" style={{ alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="mono">{p.code}</span>
                  <Pill tone={p.active ? 'success' : 'neutral'}>
                    {p.active ? 'Active' : 'Inactive'}
                  </Pill>
                </div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                  {promoValueLabel(p)} · {serviceName(p.serviceId)}
                  {p.validFrom || p.validUntil
                    ? ` · ${p.validFrom ?? '…'} → ${p.validUntil ?? '…'}`
                    : ''}
                </div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                  {/* SERVER-derived usage — never a local count. */}
                  Used {p.timesUsed}
                  {p.usageLimit != null
                    ? ` / ${p.usageLimit}`
                    : ' time' + (p.timesUsed === 1 ? '' : 's')}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                Edit
              </Button>
            </div>
          ))}
        </div>
      )}

      {creating ? (
        <CreatePromoModal
          services={services}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            toast.success('Promo code created');
          }}
        />
      ) : null}
      {editing ? (
        <EditPromoModal
          promo={editing}
          serviceName={serviceName(editing.serviceId)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Promo code updated');
          }}
        />
      ) : null}
    </Card>
  );
}

function CreatePromoModal({
  services,
  onClose,
  onCreated,
}: {
  services: Service[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const create = useCreatePromoCode();

  const [code, setCode] = useState('');
  const [type, setType] = useState<DiscountType>('percent');
  const [percent, setPercent] = useState('10');
  const [amount, setAmount] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);

  const valueBps = type === 'percent' ? percentToBps(percent) : null;
  const valuePence = type === 'fixed' ? poundsToPence(amount) : null;
  const valueOk = type === 'percent' ? valueBps != null : valuePence != null;
  const canCreate = code.trim().length >= 3 && valueOk;

  async function submit() {
    if (!canCreate) return;
    setConflict(null);
    try {
      await create.mutateAsync({
        code: code.trim(),
        type,
        valueBps: type === 'percent' ? (valueBps ?? undefined) : undefined,
        valuePence: type === 'fixed' ? (valuePence ?? undefined) : undefined,
        serviceId: serviceId || undefined,
        usageLimit: usageLimit.trim() ? Number(usageLimit) : undefined,
        validFrom: validFrom || undefined,
        validUntil: validUntil || undefined,
      });
      onCreated();
    } catch (err) {
      // Surface a duplicate-code conflict INLINE (don't lose it to a transient toast).
      if (err instanceof ApiClientError && err.isConflict) {
        setConflict(err.message || 'A promo code with that code already exists.');
        return;
      }
      toast.error(err instanceof ApiClientError ? err.message : 'Could not create the promo code.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New promo code"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canCreate || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create code'}
          </Button>
        </>
      }
    >
      <Field
        label="Code"
        error={conflict}
        hint="3–40 chars: letters, digits, hyphen or underscore."
      >
        <TextInput
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (conflict) setConflict(null);
          }}
          placeholder="SUMMER25"
        />
      </Field>

      <Field label="Type">
        <SegmentedControl<DiscountType>
          options={[
            { value: 'percent', label: 'Percent' },
            { value: 'fixed', label: 'Fixed £' },
          ]}
          value={type}
          onChange={setType}
        />
      </Field>

      {type === 'percent' ? (
        <Field label="Percent off (%)" hint="0–100%.">
          <TextInput
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="10"
          />
        </Field>
      ) : (
        <Field label="Amount off (£)">
          <TextInput
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="5.00"
          />
        </Field>
      )}

      <Field label="Applies to" hint="Scope is fixed once created.">
        <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
          <option value="">Any service</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Usage limit" hint="Leave blank for unlimited.">
        <TextInput
          inputMode="numeric"
          value={usageLimit}
          onChange={(e) => setUsageLimit(e.target.value)}
          placeholder="Unlimited"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Valid from" hint="Optional">
          <TextInput type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </Field>
        <Field label="Valid until" hint="Optional">
          <TextInput
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Edit a promo code. Only the window, usage limit and active flag are editable (matches UpdatePromoCode);
 * the value and service scope are IMMUTABLE — shown read-only so the operator sees them without being able
 * to change them.
 */
function EditPromoModal({
  promo,
  serviceName,
  onClose,
  onSaved,
}: {
  promo: PromoCode;
  serviceName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const update = useUpdatePromoCode();

  const [active, setActive] = useState(promo.active);
  const [usageLimit, setUsageLimit] = useState(
    promo.usageLimit != null ? String(promo.usageLimit) : '',
  );
  const [validFrom, setValidFrom] = useState(promo.validFrom ?? '');
  const [validUntil, setValidUntil] = useState(promo.validUntil ?? '');

  async function submit() {
    try {
      await update.mutateAsync({
        id: promo.id,
        body: {
          active,
          usageLimit: usageLimit.trim() ? Number(usageLimit) : null,
          validFrom: validFrom || null,
          validUntil: validUntil || null,
        },
      });
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update the promo code.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${promo.code}`}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {/* Value + scope are frozen at create (UpdatePromoCode can't touch them) — shown read-only. */}
      <div className="settings-list" style={{ marginBottom: 12 }}>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Value</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Fixed at creation — re-create a code to change it.
            </div>
          </div>
          <Pill tone="soft">{promoValueLabel(promo)}</Pill>
        </div>
        <div className="settings-row">
          <div>
            <div style={{ fontWeight: 600 }}>Applies to</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              Scope is fixed at creation.
            </div>
          </div>
          <Pill tone="neutral">{serviceName}</Pill>
        </div>
      </div>

      <Field label="Active">
        <Toggle checked={active} onChange={setActive} label={active ? 'Live' : 'Paused'} />
      </Field>

      <Field label="Usage limit" hint="Leave blank for unlimited.">
        <TextInput
          inputMode="numeric"
          value={usageLimit}
          onChange={(e) => setUsageLimit(e.target.value)}
          placeholder="Unlimited"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Valid from" hint="Optional">
          <TextInput type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </Field>
        <Field label="Valid until" hint="Optional">
          <TextInput
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </Field>
      </div>

      <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
        Used {promo.timesUsed} time{promo.timesUsed === 1 ? '' : 's'} so far.
      </div>
    </Modal>
  );
}
